import { ColumnDef } from '@tanstack/react-table';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { Dropdown, Image } from 'react-bootstrap';
import { ContactPerson } from 'data/users';
import { Link, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { DELETE_CUSTOMERS_REP } from 'services/api/customer-rep';
import { useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';

const handleDelete = async (customerId: string) => {
  try {
    await DELETE_CUSTOMERS_REP(customerId);
    toast.success('Representative deleted successfully');
  } catch (error) {
    console.error('Error deleting customer:', error);
    toast.error('Error deleting representative');
  }
};

export const RepTableColumns = (
  canEdit = false,
  canDelete = false
): ColumnDef<ContactPerson>[] => [
  {
    accessorKey: 'firstName',
    header: 'First Name',
    cell: ({ row: { original } }) => {
      const { firstName } = original;
      return (
        <div className="d-flex align-items-center">
          <div>{firstName}</div>
        </div>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: { style: { width: '20%' }, className: 'ps-0' },
      cellProps: { className: 'white-space-nowrap ps-0' }
    }
  },
  {
    accessorKey: 'lastName',
    header: 'Last Name',
    cell: ({ row: { original } }) => {
      const { lastName } = original;
      return (
        <div className="d-flex align-items-center">
          <div>{lastName}</div>
        </div>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: { style: { width: '20%' }, className: 'ps-0' },
      cellProps: { className: 'white-space-nowrap ps-0' }
    }
  },
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row: { original } }) => {
      const { title } = original;
      return (
        <div className="d-flex align-items-center">
          <div>{title || '-'}</div>
        </div>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: { style: { width: '15%' }, className: 'ps-0' },
      cellProps: { className: 'white-space-nowrap ps-0' }
    }
  },
  {
    id: 'companyName',
    accessorFn: row => row.client?.organizationName || 'N/A',
    header: 'Company Name',
    cell: ({ row: { original } }) => {
      const organizationName = original.client?.organizationName || 'N/A';
      const companyLogoUrl = original.client?.companyLogoUrl;

      return (
        <div className="d-flex align-items-center">
          {companyLogoUrl && (
            <Image
              src={companyLogoUrl}
              alt="Company Logo"
              className="img-fluid cursor-pointer"
              style={{
                borderRadius: '100%',
                width: '20px',
                margin: '10px',
                cursor: 'pointer'
              }}
            />
          )}
          <div>{organizationName}</div>
        </div>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: { style: { width: '25%' }, className: 'ps-0' },
      cellProps: { className: 'white-space-nowrap ps-0' }
    }
  },
  {
    accessorKey: 'mobile',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-primary-100 rounded me-2">
            <FeatherIcon
              icon="phone"
              size={16}
              className="text-primary-600 dark__text-primary-300"
            />
          </div>
          <span>Mobile</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { mobile } = original;
      return (
        <Link className="text-1000" to={`tel:${mobile}`}>
          {mobile}
        </Link>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: {
        style: { width: '15%', minWidth: '180px' },
        className: 'ps-4 pe-5 border-end'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
      }
    }
  },
  {
    accessorKey: 'email',
    header: () => {
      return (
        <div className="d-inline-flex flex-center">
          <div className="px-1 py-1 bg-success-100 rounded me-2">
            <FeatherIcon
              icon="map-pin"
              size={16}
              className="text-success-600 dark__text-success-300"
            />
          </div>
          <span>Email</span>
        </div>
      );
    },
    cell: ({ row: { original } }) => {
      const { email } = original;
      return (
        <Link className="text-1000" to={`mailto:${email}`}>
          {email ?? 'N/A'}
        </Link>
      );
    },
    enableSorting: true,
    meta: {
      headerProps: {
        style: { width: '15%' },
        className: 'ps-4 pe-5 border-end'
      },
      cellProps: {
        className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
      }
    }
  },
  {
    id: 'leadDropdown',
    cell: ({ row: { original } }) => {
      const customerId = (original as any).id || original._id;
      const navigate = useNavigate();
      const queries = useQueryClient();
      const handleEditClick = () => {
        navigate(`/home/apps/representative/edit-rep/${customerId}`);
      };
      const handleDeleteClick = async (customerId: string) => {
        try {
          if (confirm('Are you sure want to delete this resource')) {
            await handleDelete(customerId);
            queries.invalidateQueries('get_customer_reps');
            navigate(`/home/apps/representative`);
          }
        } catch (error) {
          console.error('Error handling delete:', error);
        }
      };

      if (!canEdit && !canDelete) {
        return null;
      }

      return (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <>
              {canEdit && (
                <Dropdown.Item eventKey="2" onClick={handleEditClick}>
                  Edit
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              {canDelete && (
                <Dropdown.Item
                  eventKey="4"
                  className="text-danger"
                  onClick={() => handleDeleteClick(customerId)}
                >
                  Delete
                </Dropdown.Item>
              )}
            </>
          </RevealDropdown>
        </RevealDropdownTrigger>
      );
    },
    enableSorting: false,
    meta: {
      cellProps: {
        className: 'text-end pe-0 ps-4'
      }
    }
  }
];
